// =============================================================================
// src/features/crisis/index.ts
//
// Takeover Gestao de Crise: markup (unified.html 1939-2351) + comportamento TS
//   (porte de assets/crisis.js 500 linhas, antes auto-boot por IIFE).
//
// render() devolve a marcacao fiel; initCrisisTakeover(ctx) substitui o IIFE
//   auto-bootado — main.ts passa a chamar este init() no lugar de injetar o
//   <script> legado. Guard preservado: aborta em silencio sem #crisisTakeover ou
//   #crisisLauncher (assets/crisis.js 14-16). O comportamento e dividido por SRP
//   em modulos irmaos (charts, slider, ticker, view-switch, action-plan, draft,
//   takeover-controller); este index so resolve as raizes e fia os controllers.
//
// Contrato: AppModule (src/app/types.ts). O ctx (bus/commands/map) e recebido por
//   simetria com os demais modulos; o takeover de crise nao consome o mapa.
// =============================================================================

import type { AppContext, AppModule } from '../../app/types';
import { qs } from '../../app/dom';
import { CrisisTickerController } from './crisis-ticker';
import { CrisisViewSwitch } from './crisis-view-switch';
import { bindCrisisActionPlan } from './crisis-action-plan';
import { bindCrisisDraft } from './crisis-draft';
import { bindCrisisBridge } from './crisis-reputation-bridge';
import { bindCrisisNarrative } from './crisis-narrative';
import { CrisisTakeoverController } from './crisis-takeover-controller';
import { resolveOwnerIdentity } from '../../app/owner-identity';

/** Markup do takeover. Thunk (não const) para ler resolveOwnerIdentity() em call-time
 *  (após a hidratação da identidade no bootstrap), não no load do módulo. */
const renderMarkup = (): string => `<div id="crisisTakeover" class="crisis-takeover" role="dialog" aria-modal="true" aria-labelledby="crzTitle" hidden>

  <!-- Sprite de severidade do medidor (reaproveitado pelo JS) -->
  <header class="crz-head">
    <div class="crz-brand">
      <span class="crz-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 3l7 2.5v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9v-5L12 3z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
          <path d="M8.5 13.6a3.5 3.5 0 0 1 7 0" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
          <line x1="12" y1="13.6" x2="14.3" y2="10.8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
        </svg>
      </span>
      <div>
        <div class="crz-brand-title" id="crzTitle">Gestão de Crise</div>
        <div class="crz-brand-sub">Mapa Político · Manaus</div>
      </div>
    </div>

    <div class="crz-ctx">
      <span class="crz-ctx-dot" id="crzCtxDot" title="Atenção"></span>
      <div style="min-width:0">
        <div class="crz-ctx-name">${resolveOwnerIdentity().name}</div>
        <div class="crz-ctx-meta">${resolveOwnerIdentity().meta}</div>
      </div>
    </div>

    <div class="crz-head-spacer"></div>

    <div class="crz-seg" role="group" aria-label="Cenários">
      <button type="button" class="crz-seg-btn active" data-crz-view="crisis">Crise ativa</button>
      <button type="button" class="crz-seg-btn" data-crz-view="analyzing">IA analisando</button>
      <button type="button" class="crz-seg-btn" data-crz-view="calm">Calmo</button>
    </div>

    <button type="button" class="crz-close" id="crzClose" aria-label="Voltar ao mapa">
      <i class="ti ti-x crz-ico sm" aria-hidden="true"></i> Voltar ao mapa
    </button>
  </header>

  <!-- Ticker ao vivo — feed de menções + métricas (estilo financeiro) -->
  <div class="crz-ticker" id="crzTicker" aria-label="Feed ao vivo de menções e métricas">
    <div class="crz-ticker-live">
      <span class="crz-ticker-pip" aria-hidden="true"></span> Ao vivo
    </div>
    <div class="crz-ticker-viewport">
      <div class="crz-ticker-track" id="crzTickerTrack"></div>
    </div>
    <button type="button" class="crz-ticker-toggle" id="crzTickerToggle"
            aria-label="Pausar feed" aria-pressed="false">
      <i class="ti ti-player-pause-filled crz-ico sm" aria-hidden="true"></i>
    </button>
  </div>

  <div class="crz-scroll">
    <main class="crz-main">

      <!-- ============ VIEW: CRISE ATIVA ============ -->
      <div id="crzViewCrisis" class="crz-flow">

        <!-- Panorama -->
        <section class="crz-panorama" aria-label="Panorama da situação">
          <div class="crz-panorama-top">
            <div>
              <div class="crz-eyebrow">Situação <span class="sep"></span> atualizado há 4 min</div>
              <p class="crz-lede">A repercussão sobre o <strong>contrato da reforma do pronto-socorro da Zona Leste</strong> subiu de forma moderada nas últimas 6 horas, concentrada em veículos e perfis da zona leste de Manaus. Ainda está contida, mas tende a amplificar até o fim da tarde se não houver esclarecimento factual.</p>
            </div>
            <div class="crz-gauge" role="img" aria-label="Nível de risco: Atenção, 62 de 100, tendência de alta">
              <svg viewBox="0 0 168 100" aria-hidden="true">
                <path class="crz-gauge-track" d="M 18 86 A 66 66 0 0 1 150 86" fill="none" stroke-width="11" stroke-linecap="round"/>
                <path class="crz-gauge-val" id="crzGaugeArc" d="M 18 86 A 66 66 0 0 1 150 86" fill="none" stroke-width="11" stroke-linecap="round"/>
                <circle id="crzGaugeDot" cx="0" cy="0" r="6" fill="#FFFFFF" stroke="#BA7517" stroke-width="3"/>
              </svg>
              <div class="crz-gauge-label"><i class="ti ti-alert-triangle crz-ico sm" aria-hidden="true"></i> Atenção</div>
              <div class="crz-gauge-cap">62/100 · tende a subir</div>
            </div>
          </div>

          <div class="crz-slivers">
            <div class="crz-sliver">
              <span class="crz-sliver-label">Menções (24h)</span>
              <div class="crz-sliver-row">
                <span class="crz-sliver-val">1.240</span>
                <span class="crz-trend up"><i class="ti ti-trending-up crz-ico sm" aria-hidden="true"></i> 180%</span>
                <svg width="58" height="22" viewBox="0 0 58 22" id="crzSpark1" aria-hidden="true"><polyline fill="none" stroke="#E24B4A" stroke-width="1.6" points=""/></svg>
              </div>
              <span class="crz-sliver-foot">vs. média do período</span>
            </div>
            <div class="crz-sliver">
              <span class="crz-sliver-label">Alcance</span>
              <div class="crz-sliver-row">
                <span class="crz-sliver-val">320&nbsp;mil</span>
                <span class="crz-trend up"><i class="ti ti-trending-up crz-ico sm" aria-hidden="true"></i></span>
              </div>
              <span class="crz-sliver-foot">contas potencialmente expostas</span>
            </div>
            <div class="crz-sliver">
              <span class="crz-sliver-label">Sentimento líquido</span>
              <div class="crz-sliver-row">
                <span class="crz-sliver-val" style="color:#E24B4A">−38</span>
                <span class="crz-trend down"><i class="ti ti-trending-down crz-ico sm" aria-hidden="true"></i></span>
              </div>
              <span class="crz-sliver-foot">58% negativo · 30% neutro</span>
            </div>
            <div class="crz-sliver">
              <span class="crz-sliver-label">Aceleração</span>
              <div class="crz-sliver-row">
                <span class="crz-sliver-val" style="font-size:16px">Subindo</span>
                <svg width="58" height="22" viewBox="0 0 58 22" id="crzSpark2" aria-hidden="true"><polyline fill="none" stroke="#BA7517" stroke-width="1.6" points=""/></svg>
              </div>
              <span class="crz-sliver-foot crz-source-warn"><i class="ti ti-alert-circle crz-ico sm" aria-hidden="true"></i> 1 fonte indisponível (TV local)</span>
            </div>
          </div>
        </section>

        <!-- Focus grid -->
        <div class="crz-focus-grid">

          <!-- Crise em foco -->
          <section class="crz-panel" aria-labelledby="crzCrisisTitle">
            <div class="crz-panel-head">
              <div class="crz-panel-eyebrow"><i class="ti ti-shield crz-ico sm" aria-hidden="true"></i> Crise em foco</div>
              <h2 class="crz-panel-title" id="crzCrisisTitle">Contrato da reforma do pronto-socorro da Zona Leste sob questionamento</h2>
              <div class="crz-meta-row">
                <span class="mi"><i class="ti ti-clock crz-ico sm" aria-hidden="true"></i> Detectada há 2h40 · 07h12</span>
                <span class="mi"><i class="ti ti-activity crz-ico sm" aria-hidden="true"></i> Origem digital</span>
              </div>
              <p class="crz-origin"><b>Como começou:</b> matéria do <b>Portal Amazonas Agora</b> foi ampliada por um recorte de 18 segundos de uma fala do senador, sem o contexto do edital público da obra.</p>
            </div>

            <div class="crz-panel-body">
              <!-- Stepper -->
              <div class="crz-stepper" role="list" aria-label="Fases da resposta">
                <div class="crz-step done" role="listitem">
                  <div class="crz-step-bar"></div>
                  <div class="crz-step-name"><span class="crz-pin"><i class="ti ti-check" style="font-size:10px" aria-hidden="true"></i></span> Detecção</div>
                  <span class="crz-step-time">concluída · 0–1h</span>
                </div>
                <div class="crz-step current" role="listitem">
                  <div class="crz-step-bar"></div>
                  <div class="crz-step-name"><span class="crz-pin"></span> Priorização</div>
                  <span class="crz-step-time">em curso · 1–3h</span>
                </div>
                <div class="crz-step" role="listitem">
                  <div class="crz-step-bar"></div>
                  <div class="crz-step-name"><span class="crz-pin"></span> Ação</div>
                  <span class="crz-step-time">prevista · 3–8h</span>
                </div>
                <div class="crz-step" role="listitem">
                  <div class="crz-step-bar"></div>
                  <div class="crz-step-name"><span class="crz-pin"></span> Mensuração</div>
                  <span class="crz-step-time">8–24h</span>
                </div>
              </div>

              <!-- Chart -->
              <div class="crz-chart-wrap">
                <div class="crz-chart-top">
                  <h4>Evolução das menções · 24h</h4>
                  <span class="crz-chart-legend">linha tracejada = matéria publicada (07h12)</span>
                </div>
                <div class="crz-evo-plot" id="crzEvoPlot" tabindex="0" role="slider"
                     aria-label="Evolução das menções nas últimas 24 horas"
                     aria-valuemin="0" aria-valuemax="23" aria-valuenow="23"
                     aria-valuetext="108 menções, agora">
                  <svg class="crz-evo" viewBox="0 0 600 132" preserveAspectRatio="none" aria-hidden="true">
                    <defs>
                      <linearGradient id="crzEvoFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stop-color="#BA7517" stop-opacity="0.22"/>
                        <stop offset="100%" stop-color="#BA7517" stop-opacity="0"/>
                      </linearGradient>
                    </defs>
                    <path class="crz-evo-area" id="crzEvoArea" d=""/>
                    <path class="crz-evo-line" id="crzEvoLine" d="" vector-effect="non-scaling-stroke"/>
                    <line class="crz-evo-marker" id="crzEvoMark" x1="0" y1="6" x2="0" y2="132" vector-effect="non-scaling-stroke"/>
                  </svg>
                  <div class="crz-evo-guide" aria-hidden="true"></div>
                  <div class="crz-evo-dot" aria-hidden="true"></div>
                  <div class="crz-evo-tip" aria-hidden="true">
                    <span class="crz-evo-tip-val">—</span><span class="crz-evo-tip-time">—</span>
                  </div>
                </div>
                <div class="crz-axis-x"><span>ontem 12h</span><span>18h</span><span>00h</span><span>06h</span><span>agora</span></div>
                <p class="crz-sr-only" id="crzEvoLive" aria-live="polite"></p>
              </div>

              <!-- Sentimento -->
              <div class="crz-sent">
                <h4>Sentimento das menções</h4>
                <div class="crz-sent-bar" role="img" aria-label="58% negativo, 30% neutro, 12% positivo">
                  <div class="crz-sent-neg" style="width:58%"></div>
                  <div class="crz-sent-neu" style="width:30%"></div>
                  <div class="crz-sent-pos" style="width:12%"></div>
                </div>
                <div class="crz-sent-legend">
                  <span><i style="background:#E24B4A"></i>Negativo 58%</span>
                  <span><i style="background:#B8B7B0"></i>Neutro 30%</span>
                  <span><i style="background:#1D9E75"></i>Positivo 12%</span>
                </div>
              </div>

              <!-- Amplificadores -->
              <div class="crz-amp">
                <h4>Quem está amplificando</h4>
                <div class="crz-amp-list">
                  <div class="crz-amp-item">
                    <span class="crz-amp-ic"><i class="ti ti-at crz-ico" aria-hidden="true"></i></span>
                    <div><div class="crz-amp-name">@oposicao_local</div><div class="crz-amp-type">Perfil de oposição · X / Instagram</div></div>
                    <div class="crz-amp-share"><div class="crz-amp-pct">34%</div><div class="crz-amp-reach">~140 mil alcance</div></div>
                  </div>
                  <div class="crz-amp-item">
                    <span class="crz-amp-ic"><i class="ti ti-world crz-ico" aria-hidden="true"></i></span>
                    <div><div class="crz-amp-name">Portal Amazonas Agora</div><div class="crz-amp-type">Veículo regional · web</div></div>
                    <div class="crz-amp-share"><div class="crz-amp-pct">22%</div><div class="crz-amp-reach">~95 mil alcance</div></div>
                  </div>
                  <div class="crz-amp-item">
                    <span class="crz-amp-ic"><i class="ti ti-speakerphone crz-ico" aria-hidden="true"></i></span>
                    <div><div class="crz-amp-name">Vereador adversário</div><div class="crz-amp-type">Liderança política · redes</div></div>
                    <div class="crz-amp-share"><div class="crz-amp-pct">14%</div><div class="crz-amp-reach">~60 mil alcance</div></div>
                  </div>
                </div>
              </div>

              <!-- Menções (disclosure) -->
              <details class="crz-disc">
                <summary><i class="ti ti-chevron-right crz-ico sm chev" aria-hidden="true"></i> Ver menções representativas (3)</summary>
                <div class="crz-disc-inner">
                  <div class="crz-quote"><p>"Cadê a explicação sobre o valor da obra do pronto-socorro? O contribuinte merece transparência."</p><div class="crz-q-meta"><i class="ti ti-at crz-ico sm" aria-hidden="true"></i> @cidadao_zonaleste · 2,1 mil interações</div></div>
                  <div class="crz-quote"><p>"Matéria levanta dúvidas sobre o contrato da reforma; gabinete ainda não se pronunciou."</p><div class="crz-q-meta"><i class="ti ti-world crz-ico sm" aria-hidden="true"></i> Portal Amazonas Agora · web</div></div>
                  <div class="crz-quote"><p>"Vamos protocolar pedido de informação no Senado sobre essa obra."</p><div class="crz-q-meta"><i class="ti ti-speakerphone crz-ico sm" aria-hidden="true"></i> Parlamentar adversário · X</div></div>
                </div>
              </details>
            </div>
          </section>

          <!-- Plano de contingência IA -->
          <aside class="crz-ai" aria-labelledby="crzAiTitle">
            <div class="crz-ai-head">
              <div class="crz-ai-badge-row">
                <span class="crz-ai-spark"><i class="ti ti-sparkles crz-ico" aria-hidden="true"></i></span>
                <div>
                  <div class="crz-ai-kicker">Assistido por IA</div>
                  <h2 class="crz-ai-title" id="crzAiTitle">Plano de contingência</h2>
                </div>
                <div class="crz-ai-conf">
                  <div class="crz-ai-conf-label">Confiança: média</div>
                  <div class="crz-ai-conf-bar"><div class="crz-ai-conf-fill"></div></div>
                </div>
              </div>
              <div class="crz-ai-disclaimer"><i class="ti ti-info-circle crz-ico sm" aria-hidden="true"></i> Sugestões geradas por IA a partir das menções. Revise antes de acionar.</div>
            </div>

            <!-- Conteúdo do plano (estado pronto) -->
            <div id="crzAiReady">
              <div class="crz-ai-reading">
                <span class="rk">Leitura da situação</span>
                O dano vem de um recorte sem contexto, não de um fato novo. A janela para conter é hoje, antes do ciclo da TV local à noite. Uma resposta factual e rápida, com porta-voz técnico, tende a reverter o sentimento.
              </div>

              <div class="crz-plan" id="crzPlanList">
                <div class="crz-phase">
                  <div class="crz-phase-head"><span class="crz-phase-tag">AGORA</span><span class="crz-phase-when">0–1h · enquanto está contido</span></div>

                  <div class="crz-action" data-id="a1">
                    <div class="crz-action-status"><span class="crz-a-dot"></span></div>
                    <div class="crz-action-body">
                      <p class="crz-action-text">Publicar nota curta com o cronograma e o valor real do contrato, com link para o edital público.</p>
                      <div class="crz-action-meta">
                        <span class="crz-chip crz-chip-rationale"><i class="ti ti-sparkles crz-ico sm" aria-hidden="true"></i> Fato supera o recorte</span>
                        <span class="crz-chip">Site + redes</span>
                      </div>
                    </div>
                    <div class="crz-action-controls"></div>
                  </div>

                  <div class="crz-action" data-id="a2">
                    <div class="crz-action-status"><span class="crz-a-dot"></span></div>
                    <div class="crz-action-body">
                      <p class="crz-action-text">Acionar o secretário de obras como porta-voz técnico do tema.</p>
                      <div class="crz-action-meta">
                        <span class="crz-chip crz-chip-rationale"><i class="ti ti-sparkles crz-ico sm" aria-hidden="true"></i> Voz técnica reduz politização</span>
                        <span class="crz-chip">Imprensa</span>
                      </div>
                    </div>
                    <div class="crz-action-controls"></div>
                  </div>

                  <div class="crz-action" data-id="a3">
                    <div class="crz-action-status"><span class="crz-a-dot"></span></div>
                    <div class="crz-action-body">
                      <p class="crz-action-text">Não responder o perfil @oposicao_local diretamente.</p>
                      <div class="crz-action-meta">
                        <span class="crz-chip crz-chip-rationale"><i class="ti ti-sparkles crz-ico sm" aria-hidden="true"></i> Evita ampliar o alcance</span>
                      </div>
                    </div>
                    <div class="crz-action-controls"></div>
                  </div>
                </div>

                <div class="crz-phase">
                  <div class="crz-phase-head"><span class="crz-phase-tag">1–3h</span><span class="crz-phase-when">priorização e reforço</span></div>

                  <div class="crz-action" data-id="a4">
                    <div class="crz-action-status"><span class="crz-a-dot"></span></div>
                    <div class="crz-action-body">
                      <p class="crz-action-text">Enviar release com dados do contrato aos 3 veículos regionais de maior alcance.</p>
                      <div class="crz-action-meta"><span class="crz-chip crz-chip-rationale"><i class="ti ti-sparkles crz-ico sm" aria-hidden="true"></i> Ocupa o vácuo de informação</span><span class="crz-chip">Assessoria</span></div>
                    </div>
                    <div class="crz-action-controls"></div>
                  </div>

                  <div class="crz-action" data-id="a5">
                    <div class="crz-action-status"><span class="crz-a-dot"></span></div>
                    <div class="crz-action-body">
                      <p class="crz-action-text">Alinhar 2 lideranças aliadas para reforço coordenado nas redes.</p>
                      <div class="crz-action-meta"><span class="crz-chip crz-chip-rationale"><i class="ti ti-sparkles crz-ico sm" aria-hidden="true"></i> Equilibra a conversa</span><span class="crz-chip">Articulação</span></div>
                    </div>
                    <div class="crz-action-controls"></div>
                  </div>
                </div>

                <div class="crz-phase">
                  <div class="crz-phase-head"><span class="crz-phase-tag">3–8h</span><span class="crz-phase-when">só se persistir</span></div>
                  <div class="crz-action" data-id="a6">
                    <div class="crz-action-status"><span class="crz-a-dot"></span></div>
                    <div class="crz-action-body">
                      <p class="crz-action-text">Agendar visita à obra com a imprensa, mostrando o andamento real.</p>
                      <div class="crz-action-meta"><span class="crz-chip crz-chip-rationale"><i class="ti ti-sparkles crz-ico sm" aria-hidden="true"></i> Agenda positiva e verificável</span><span class="crz-chip">Agenda</span></div>
                    </div>
                    <div class="crz-action-controls"></div>
                  </div>
                </div>
              </div>

              <!-- Cenários e-se -->
              <details class="crz-disc">
                <summary style="padding:14px 20px 6px"><i class="ti ti-chevron-right crz-ico sm chev" aria-hidden="true"></i> Cenários de contingência (e se…)</summary>
                <div class="crz-scen crz-disc-inner">
                  <div class="crz-scenario">
                    <span class="crz-scen-ic"><i class="ti ti-device-tv crz-ico sm" aria-hidden="true"></i></span>
                    <div><div class="crz-scen-if">Se o tema chegar à TV local</div><div class="crz-scen-then">Elevar o porta-voz para o próprio senador e preparar um dossiê de 1 página para a imprensa.</div></div>
                  </div>
                  <div class="crz-scenario">
                    <span class="crz-scen-ic"><i class="ti ti-scale crz-ico sm" aria-hidden="true"></i></span>
                    <div><div class="crz-scen-if">Se a oposição protocolar pedido formal</div><div class="crz-scen-then">Resposta jurídica imediata e publicação proativa de toda a documentação do contrato.</div></div>
                  </div>
                  <div class="crz-scenario">
                    <span class="crz-scen-ic"><i class="ti ti-trending-down crz-ico sm" aria-hidden="true"></i></span>
                    <div><div class="crz-scen-if">Se o sentimento cair abaixo de −50</div><div class="crz-scen-then">Pausar conteúdo positivo não relacionado por 24h para não soar tom-surdo.</div></div>
                  </div>
                </div>
              </details>

              <div class="crz-ai-foot">
                <div class="crz-ai-foot-row">
                  <span class="crz-approved-count"><b id="crzApprovedN">0</b> de <span id="crzTotalN">6</span> ações aprovadas</span>
                  <button class="crz-btn crz-btn-generate" id="crzGenBtn"><i class="ti ti-sparkles crz-ico sm" aria-hidden="true"></i> Gerar rascunho da nota</button>
                </div>
                <div id="crzDraftSlot"></div>
              </div>
            </div>

            <!-- Estado: IA analisando -->
            <div id="crzAiAnalyzing" hidden>
              <div class="crz-ai-analyzing">
                <div class="crz-analyzing-note"><i class="ti ti-sparkles crz-ico spark-pulse" aria-hidden="true"></i> A IA está analisando 1.240 menções e montando o plano…</div>
                <div class="crz-sk-line crz-skeleton" style="width:90%"></div>
                <div class="crz-sk-line crz-skeleton" style="width:78%"></div>
                <div class="crz-sk-row"><span class="crz-a-dot crz-skeleton" style="border:none"></span><div style="flex:1"><div class="crz-sk-line crz-skeleton" style="width:96%;margin:2px 0"></div><div class="crz-sk-line crz-skeleton" style="width:54%;margin:6px 0 2px"></div></div></div>
                <div class="crz-sk-row"><span class="crz-a-dot crz-skeleton" style="border:none"></span><div style="flex:1"><div class="crz-sk-line crz-skeleton" style="width:88%;margin:2px 0"></div><div class="crz-sk-line crz-skeleton" style="width:48%;margin:6px 0 2px"></div></div></div>
                <div class="crz-sk-row"><span class="crz-a-dot crz-skeleton" style="border:none"></span><div style="flex:1"><div class="crz-sk-line crz-skeleton" style="width:92%;margin:2px 0"></div><div class="crz-sk-line crz-skeleton" style="width:60%;margin:6px 0 2px"></div></div></div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <!-- ============ VIEW: CALMO ============ -->
      <div id="crzViewCalm" class="crz-calm" hidden>
        <section class="crz-calm-hero">
          <div class="crz-calm-shield"><i class="ti ti-shield" style="font-size:28px" aria-hidden="true"></i></div>
          <div>
            <div class="crz-calm-status"><span class="pip"></span> Monitoramento normal</div>
            <p class="crz-calm-lede">Nenhuma crise ativa no momento. Nas últimas 24 horas, 3 temas foram acompanhados e nenhum ultrapassou o limite de atenção.</p>
          </div>
        </section>

        <div class="crz-calm-grid">
          <div class="crz-calm-card">
            <h4>Pulso das últimas 24h</h4>
            <div class="crz-watch-item"><span class="w-name">Menções totais</span><span class="w-val">412 · estável</span></div>
            <div class="crz-watch-item"><span class="w-name">Sentimento líquido</span><span class="w-val" style="color:#3B6D11;font-weight:600">+11</span></div>
            <div class="crz-watch-item"><span class="w-name">Maior pico</span><span class="w-val">agenda na zona leste</span></div>
          </div>
          <div class="crz-calm-card">
            <h4>Temas observados</h4>
            <div class="crz-watch-item"><span class="pip"></span><span class="w-name">Reforma do pronto-socorro</span><span class="w-val">baixo</span></div>
            <div class="crz-watch-item"><span class="pip"></span><span class="w-name">Projeto de mobilidade</span><span class="w-val">baixo</span></div>
            <div class="crz-watch-item"><span class="pip"></span><span class="w-name">Repasse à educação</span><span class="w-val">baixo</span></div>
          </div>
          <div class="crz-calm-card">
            <h4>Prontidão</h4>
            <div class="crz-watch-item"><span class="w-name">Varredura</span><span class="w-val">contínua · 1M+ fontes</span></div>
            <div class="crz-watch-item"><span class="w-name">Alertas</span><span class="w-val">WhatsApp + e-mail</span></div>
            <div class="crz-watch-item"><span class="w-name">Equipe de plantão</span><span class="w-val">2 pessoas</span></div>
          </div>
        </div>
      </div>

    </main>
  </div>
</div>`;

/** Devolve a string HTML literal deste bloco. */
export function buildCrisisTakeoverMarkup(): string {
  return renderMarkup();
}

/** True se o usuario pediu menos animacao (matchMedia, assets/crisis.js 18). */
function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Liga o comportamento do takeover apos a marcacao estar montada no DOM.
 * Porte do IIFE de assets/crisis.js: resolve raizes, aborta sem elas (guard 14-16)
 * e fia ticker -> view-switch -> action-plan -> draft -> takeover (ordem do legado:
 * o setView inicial nao roda no boot; tudo e ligado e o desenho ocorre na abertura).
 */
export function initCrisisTakeover(_ctx: AppContext): void {
  const takeover = qs<HTMLElement>('#crisisTakeover');
  const launcher = qs<HTMLElement>('#crisisLauncher');
  if (!takeover || !launcher) return;
  const reduceMotion = prefersReducedMotion();

  const ticker = new CrisisTickerController(reduceMotion);
  ticker.bindToggle();

  const viewSwitch = new CrisisViewSwitch(takeover, ticker, reduceMotion);
  viewSwitch.bind();

  bindCrisisActionPlan(takeover);
  bindCrisisBridge(takeover);
  bindCrisisNarrative(takeover);
  bindCrisisDraft(reduceMotion);

  new CrisisTakeoverController(takeover, launcher, ticker, viewSwitch).bind();
}

/**
 * Modulo do takeover de Crise. render() = marcacao fiel; init() = comportamento
 * portado. Exportado como AppModule para o main.ts registrar como os demais
 * modulos (substitui o auto-boot do IIFE legado).
 */
export const CrisisTakeover: AppModule = {
  id: 'feature-crisis-takeover',
  render: buildCrisisTakeoverMarkup,
  init: initCrisisTakeover,
};
